import Keycloak, { KeycloakConfig } from "keycloak-js";
import {
  AdditionalTravelBuddyFieldsConfig,
  CommunicationChannel,
  FeatureFlags,
  KeycloakNativeConfig,
  KioskConfig,
  LocaleMappingConfig,
  PrismicConfig,
  PropertyConfig,
  PWA,
  SearchConfig,
  ThemeOverride
} from "../../api";
import { SecondScreenDevicesConfig } from "../../api/dto/second-screen-devices-config";

export enum ApiVersionEnum {
  V1 = "v1",
  V2 = "v2"
}

export enum LogLevel {
  NONE = "NONE",
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR"
}

export enum PmsType {
  APALEO = "APALEO",
  MEWS = "MEWS",
  OHIP = "OHIP"
}

export interface TenantContextValue {
  tenantId: string;
  theme: string;
  keycloak: Keycloak;
  keycloakConfig: KeycloakConfig;
  adyen?: { clientKey: string; environment: string };
  features?: FeatureFlags;
  allowedBackofficeLanguages?: string[];
  allowedLanguages?: string[];
  allowedLocales?: string[];
  apiKey?: string;
  urls: {
    bookingAppUrl: string;
    apaleoUrl: string;
    zeebeUrl: string;
    magicSupportUrl: string;
    magicSuggestFeatureUrl: string;
    messagebirdUrl: string;
    pmsUrl: string;
  };
  pwa?: PWA;
  prismic?: PrismicConfig;
  commonPrismic?: PrismicConfig;
  toneOfVoice?: string;
  gtmId?: string;
  googleMaps?: {
    apiKey?: string;
  };
  routesWithAuthToken: string[];
  search?: SearchConfig;
  enabledCommunicationChannels: CommunicationChannel[];
  earliestCheckinOffset: number;
  properties?: { [propertyId: string]: PropertyConfig };
  keycloakNative?: KeycloakNativeConfig;
  locale?: LocaleMappingConfig;
  version: ApiVersionEnum;
  preferredCountries?: string[];
  logLevel: LogLevel;
  disabledCountriesForIdentification: string[];
  disabledCountriesForPassportUpload: string[];
  pms?: PmsType;
  kiosk: Record<string, KioskConfig>;
  secondScreenDevices: Record<string, SecondScreenDevicesConfig>;
  themeOverride?: ThemeOverride;
  additionalTravelBuddyFields?: AdditionalTravelBuddyFieldsConfig;
}

export const tenantDefaultContextValue: TenantContextValue = {
  tenantId: "",
  theme: "",
  keycloak: {} as Keycloak,
  keycloakConfig: {} as KeycloakConfig,
  adyen: { clientKey: "", environment: "" },
  features: {
    authEnabled: false,
    enableExtendStay: false,
    bookingAppIntegrated: false,
    shopEnabled: false,
    searchEnabled: false,
    hohDemoEnabled: false,
    cookiesEnabled: false,
    reservationTableEnabled: false,
    searchByLocationEnabled: false,
    multiPropertyBookingEnabled: false,
    bookedServicesEnabled: false,
    showVat: false,
    tasksEnabled: false,
    unifiedMessagingEnabled: false,
    breakfastViewEnabled: false,
    boxEnabled: false,
    disableSVGThemeOverriding: false,
    documentsUploadEnabled: false,
    payFromBookingOverviewDisabled: false,
    diverseGenderEnabled: true,
    tfeFeatureEnabled: false,
    imsFeatureEnabled: false,
    arrivalsWithoutInHouseEnabled: false,
    maintenanceEnabled: false,
    reverseDisplaySearchUnitGroupPageEnabled: false,
    originalImageSizeForPropertyFeaturesEnabled: false,
    payAtCheckoutEnabled: false,
    selfPouringStationEnabled: false,
    privateDoorsDisabled: false,
    payAtCheckinEnabled: false,
    marketingConsentEnabled: false,
    marketingConsentDoubleOptinEnabled: false,
    guestListEnabled: false,
    ibeOnly: false,
    housekeepingV2Enabled: false,
    travelBuddyIsMinorValidationEnabled: false,
    checkoutFlowEnabled: false,
    addToHomeScreenHidden: false,
    guestCheckInIntoPMSDisabled: false,
    secondScreenEnabled: false,
    genderDisplayDisabled: false,
    checkinWizardEnabled: false,
    billingTabEnabled: false,
    findReservationByConfirmationNumberEnabled: false
  },
  allowedBackofficeLanguages: ["en", "de"],
  allowedLanguages: ["en", "de", "it", "fr"],
  allowedLocales: ["en-us", "de-ch"],
  apiKey: "",
  googleMaps: {
    apiKey: ""
  },
  pwa: {} as PWA,
  toneOfVoice: undefined,
  urls: {
    bookingAppUrl: "",
    apaleoUrl: "",
    zeebeUrl: "",
    magicSupportUrl: "",
    magicSuggestFeatureUrl: "",
    messagebirdUrl: "",
    pmsUrl: ""
  },
  prismic: undefined,
  routesWithAuthToken: [],
  search: {
    defaultArrivalDateDelta: 0,
    defaultStayLength: 1,
    defaultAdults: 1,
    defaultChildrenAges: [],
    adultsOnly: false,
    childrenAgesConfig: { minAge: 0, maxAge: 12 },
    maxReservationsPerBooking: undefined,
    deltaEndOfDayProcessing: undefined
  },
  enabledCommunicationChannels: [CommunicationChannel.EMAIL, CommunicationChannel.SMS],
  earliestCheckinOffset: 8,
  keycloakNative: { iosReturnURL: "", androidReturnURL: "" },
  locale: {},
  version: ApiVersionEnum.V1,
  preferredCountries: ["AT", "FR", "DE", "IT", "LI"],
  disabledCountriesForIdentification: [],
  disabledCountriesForPassportUpload: [],
  logLevel: LogLevel.NONE,
  pms: undefined,
  kiosk: {},
  secondScreenDevices: {},
  additionalTravelBuddyFields: undefined
};
